import {
    FC,
    ReactElement,
    useMemo,
    useState,
} from 'react';

import { useDebounce } from 'react-use';

import { debounceDelay, longDebounceDelay } from '../../../constants';
import { SchoolExperiencesCard } from '../../../containers';
import { transformEducationToSearchableOption } from '../../../models/@competentNL/Educations/EducationsTransformers';
import { SkillBase, SkillsFormData } from '../../../models/@competentNL/Skills/Skills';
import { SchoolExperience, SchoolExperienceFormData } from '../../../models/SchoolExperience/SchoolExperience';
import { setSkillsIsSuccessful as setSkillsStepIsSuccessful } from '../../../redux/competentNL/competentNL';
import { fetchCompetentNLEducation, fetchCompetentNLEducations, fetchCompetentNLSkills } from '../../../redux/competentNL/competentNLActions';
import {
    setCreateIsSuccessful as setSchoolExperienceCreateIsSuccessful,
    setUpdateIsSuccessful as setSchoolExperienceUpdateIsSuccessful,
} from '../../../redux/schoolExperience/schoolExperience';
import { addSchoolExperience, deleteSchoolExperience, editSchoolExperience } from '../../../redux/schoolExperience/schoolExperienceActions';
import { updateCandidateSkills } from '../../../redux/skills/skillsActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';
import { SearchableOption } from '../../../types';

interface ConnectedSchoolExperiencesCardProps {
    className?: string;
}

const ConnectedSchoolExperiencesCard: FC<ConnectedSchoolExperiencesCardProps> = ({
    className = '',
}): ReactElement => {
    const dispatch = useTypedDispatch();

    const candidate = useTypedSelector(state => state.candidateReducer.candidate);
    const user = useTypedSelector(state => state.userReducer.user);

    const skillsPassportIsLoading = useTypedSelector(state => state.skillsPassportReducer.isLoading);
    const skillsPassportError = useTypedSelector(state => state.skillsPassportReducer.error);
    const schoolExperiences = useTypedSelector(state => state.skillsPassportReducer.schoolExperiences);
    const skills = useTypedSelector(state => state.skillsPassportReducer.careerSkills);

    const schoolExperienceIsLoading = useTypedSelector(state => state.schoolExperienceReducer.isLoading);
    const schoolExperienceCreateIsSuccessful = useTypedSelector(state => state.schoolExperienceReducer.createIsSuccessful);
    const schoolExperienceUpdateIsSuccessful = useTypedSelector(state => state.schoolExperienceReducer.updateIsSuccessful);
    const schoolExperienceError = useTypedSelector(state => state.schoolExperienceReducer.error);

    const educations = useTypedSelector(state => state.competentNLReducer.educations);
    const education = useTypedSelector(state => state.competentNLReducer.education);
    const skillsIsLoading = useTypedSelector(state => state.competentNLReducer.skillsIsLoading);
    const skillsStepIsSuccessful = useTypedSelector(state => state.competentNLReducer.skillsIsSuccessful);
    const skillBases = useTypedSelector(state => state.competentNLReducer.skillBases);
    const skillsError = useTypedSelector(state => state.competentNLReducer.skillsError);

    const [fieldOfStudyQuery, setFieldOfStudyQuery] = useState<string>('');
    const [skillQuery, setSkillQuery] = useState<string>('');

    const fieldOfStudyOptions = useMemo((): SearchableOption[] => (
        // TODO: Might need to filter on selected education level when CompetentNL features multiple education levels
        educations.map(transformEducationToSearchableOption)
    ), [educations]);

    const educationSkillOptions = useMemo((): SkillBase[] => {
        if (!education) {
            return skillBases;
        }

        return education.essentialSkills;
    }, [education]);

    useDebounce((): void => {
        if (fieldOfStudyQuery) {
            dispatch(fetchCompetentNLEducations(fieldOfStudyQuery));
        }
    }, debounceDelay, [fieldOfStudyQuery]);

    useDebounce((): void => {
        if (skillQuery.length > 1) {
            dispatch(fetchCompetentNLSkills(skillQuery));
        }
    }, longDebounceDelay, [skillQuery]);

    const handleSelectFieldOfStudy = (educationId: string): void => {
        dispatch(fetchCompetentNLEducation(educationId));
    };

    const handleAddSchoolExperience = (schoolExperienceFormData: SchoolExperienceFormData): void => {
        if (candidate?.uuid) dispatch(addSchoolExperience(schoolExperienceFormData, candidate.uuid));
    };

    const handleEditSchoolExperience = (schoolExperienceFormData: SchoolExperienceFormData): void => {
        if (candidate?.uuid) dispatch(editSchoolExperience(schoolExperienceFormData, candidate.uuid));
    };

    const handleDeleteSchoolExperience = (schoolExperience: SchoolExperience): void => {
        if (candidate?.uuid) dispatch(deleteSchoolExperience(schoolExperience, candidate.uuid));
    };

    const handleAddSkills = (skillsFormData: SkillsFormData): void => {
        if (candidate?.uuid) dispatch(updateCandidateSkills(skillsFormData, candidate.uuid));
    };

    const handleIsSuccessfulReset = (): void => {
        dispatch(setSchoolExperienceCreateIsSuccessful(false));
        dispatch(setSchoolExperienceUpdateIsSuccessful(false));
        dispatch(setSkillsStepIsSuccessful(false));
    };

    return (
        <SchoolExperiencesCard
            isLoading={skillsPassportIsLoading}
            schoolExperienceIsLoading={schoolExperienceIsLoading}
            schoolExperienceCreateIsSuccessful={schoolExperienceCreateIsSuccessful}
            schoolExperienceUpdateIsSuccessful={schoolExperienceUpdateIsSuccessful}
            skillsIsLoading={skillsIsLoading}
            skillsStepIsSuccessful={skillsStepIsSuccessful}
            hasError={!!skillsPassportError}
            schoolExperienceError={schoolExperienceError}
            skillOptionsError={skillsError}
            userRoles={user?.roles}
            schoolExperiences={schoolExperiences}
            skills={skills}
            fieldOfStudyOptions={fieldOfStudyOptions}
            skillOptions={educationSkillOptions}
            searchedSkillOptions={skillBases}
            onFieldOfStudyInput={setFieldOfStudyQuery}
            onSelectFieldOfStudy={handleSelectFieldOfStudy}
            onAddSchoolExperience={handleAddSchoolExperience}
            onEditSchoolExperience={handleEditSchoolExperience}
            onDeleteSchoolExperience={handleDeleteSchoolExperience}
            onSearchSkills={setSkillQuery}
            onAddSkills={handleAddSkills}
            onIsSuccessfulReset={handleIsSuccessfulReset}
            className={className}
        />
    );
};

export default ConnectedSchoolExperiencesCard;
